/** Step 3: which tab this ticket sits on, and where in that tab. */
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/solid";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { TicketingItem } from "../../../../types/tickets";
import { DEFAULT_TAB_NAME, groupItemsByTab } from "../../ticket-items/tabLayout";
import { UPDATE_ITEMS_TICKETING_MANY } from "../queries";
import type { StepProps } from "../TicketingSetupPage";
import { isPublicTicket } from "../types";
import { Card, CheckRow, Chip, StepFooter, money } from "../ui";

const client = createApolloClient();
const { getState: getUserState } = userStore;
const NONE = "";

export const PlacementStep = ({ data, refresh, ticket, ticketId, checks }: StepProps) => {
  const t = ticket!;
  const tabs = useMemo(() => [...data.tabs].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)), [data.tabs]);
  const [tabId, setTabId] = useState<string>(t.tab_id ? String(t.tab_id) : NONE);
  // Ordered ids of the tickets on the chosen tab, this ticket included.
  const [order, setOrder] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const byId = useMemo(() => new Map(data.tickets.map((x) => [x.id, x])), [data.tickets]);
  const membersOf = (tab: string) =>
    data.tickets
      .filter((x) => x.id !== t.id && (tab === NONE ? !x.tab_id : String(x.tab_id) === tab))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name))
      .map((x) => x.id);

  useEffect(() => {
    const others = membersOf(tabId);
    const sameTab = (tabId === NONE ? !t.tab_id : String(t.tab_id) === tabId);
    if (sameTab) {
      // keep the saved position
      const all = [...others, t.id].sort((a, b) => ((byId.get(a)?.sort_order ?? 0) - (byId.get(b)?.sort_order ?? 0)) || String(byId.get(a)?.name).localeCompare(String(byId.get(b)?.name)));
      setOrder(all);
    } else {
      setOrder([...others, t.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, data.tickets]);

  const idx = order.indexOf(t.id);
  const move = (d: -1 | 1) => setOrder((p) => { const i = p.indexOf(t.id); const to = i + d; if (i < 0 || to < 0 || to >= p.length) return p; const n = [...p]; n.splice(i, 1); n.splice(to, 0, t.id); return n; });

  const dirty = (t.tab_id ? String(t.tab_id) : NONE) !== tabId || order.some((id, i) => (byId.get(id)?.sort_order ?? 0) !== i);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const newTabId = tabId === NONE ? null : Number(tabId);
      const updates = order.map((id, i) => ({
        where: { id: { _eq: id } },
        _set: { sort_order: i, ...(id === t.id ? { tab_id: newTabId } : {}), last_updated_by: getUserState().user?.id },
      }));
      await client.mutate({ mutation: UPDATE_ITEMS_TICKETING_MANY, variables: { updates } });
      await client.clearStore();
      toast.success("Placement saved");
      refresh();
    } catch (err) {
      toast.error("Failed to save placement: " + err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Public preview built from the draft placement.
  const previewItems: TicketingItem[] = useMemo(() => {
    const chosen = tabs.find((x) => String(x.id) === tabId) ?? null;
    return data.tickets
      .filter(isPublicTicket)
      .map((x) => {
        if (x.id !== t.id && !order.includes(x.id)) return x;
        const i = order.indexOf(x.id);
        return { ...x, sort_order: i, tab_id: chosen?.id ?? null, tab: chosen && chosen.is_active ? chosen : null };
      });
  }, [data.tickets, order, tabId, tabs, t.id]);
  const previewGroups = useMemo(() => groupItemsByTab(previewItems), [previewItems]);
  const stepChecks = checks.filter((c) => c.step === "placement");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
      <div className="flex flex-col gap-3">
        <Card title="Placement" subtitle={`Which tab "${t.name}" sits on, and where in that tab. Tabs themselves are managed under Event Setup.`}>
          {!isPublicTicket(t) && <div className="text-xs text-gray-600 bg-gray-50 rounded-md px-3 py-2">This ticket is not on public sale, so the placement only matters if that changes.</div>}
          <div className="max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tab</label>
            <select value={tabId} onChange={(e) => setTabId(e.target.value)} className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-red-500 focus:ring-red-500">
              {tabs.map((tab) => (
                <option key={tab.id} value={String(tab.id)}>{tab.name}{tab.is_active ? "" : " (hidden)"}</option>
              ))}
              <option value={NONE}>{tabs.length ? `— none (default "${DEFAULT_TAB_NAME}" tab) —` : "— no tabs on this event —"}</option>
            </select>
            {tabs.length === 0 && <div className="text-xs text-gray-500 mt-1">Create tabs under Event Setup on the overview page.</div>}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Order within this tab</div>
            <div className="flex flex-col divide-y divide-gray-100 border-t border-b border-gray-100 max-w-xl">
              {order.map((id, i) => {
                const x = byId.get(id);
                if (!x) return null;
                const me = id === t.id;
                return (
                  <div key={id} className={`flex items-center gap-3 py-1.5 px-2 ${me ? "bg-red-50" : ""}`}>
                    <span className="w-5 text-xs text-gray-400 text-right">{i + 1}</span>
                    <span className={`flex-1 min-w-0 truncate text-sm ${me ? "font-semibold text-gray-900" : "text-gray-700"}`}>{x.name}</span>
                    {!isPublicTicket(x) && <Chip tone="gray">{x.is_active ? "not public" : "inactive"}</Chip>}
                    <span className="text-xs text-gray-500">{money(x.price)}</span>
                    {me && (
                      <div className="flex gap-1">
                        <button type="button" onClick={() => move(-1)} disabled={idx <= 0} aria-label="Move up" className="h-7 w-7 inline-flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30"><ArrowUpIcon className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => move(1)} disabled={idx >= order.length - 1} aria-label="Move down" className="h-7 w-7 inline-flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30"><ArrowDownIcon className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
        <StepFooter ticketId={ticketId} back="modifiers" next="gates" nextLabel={dirty ? "Save and continue to Gates" : "Continue to Gates"} onNext={save} onSave={save} busy={saving} />
      </div>
      <div className="flex flex-col gap-4">
        <Card title="Public preview" subtitle="Same order the site uses.">
          {previewGroups.length === 0 ? <div className="text-sm text-gray-500">No public tickets.</div> : (
            <div className="border border-gray-200 rounded-md p-3 flex flex-col gap-3">
              <div className="flex flex-wrap gap-1.5">
                {previewGroups.map((g, i) => <span key={g.key} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${i === 0 ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700"}`}>{g.name}</span>)}
              </div>
              {previewGroups.map((g) => (
                <div key={g.key} className="flex flex-col gap-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase">{g.name}</div>
                  {g.items.map((x) => <div key={x.id} className={`flex justify-between text-sm ${x.id === t.id ? "font-semibold text-gray-900" : "text-gray-700"}`}><span className="truncate">{x.name}</span><span className="text-gray-500 ml-2">{money(x.price)}</span></div>)}
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card title="Checks for this step">
          {stepChecks.length === 0 ? <div className="text-sm text-gray-500">Nothing to report.</div> : stepChecks.map((c, i) => <CheckRow key={i} check={c} ticketId={ticketId} />)}
        </Card>
      </div>
    </div>
  );
};
